import { ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgIf } from '@angular/common';
import { TourService } from './tour.service';
import { TourStep } from './tour-steps';

interface View { text: string; where: string; back: boolean; next: string | null; }

/**
 * The tour's note (TourService), as the desktop draws it (wd-desktop/app/ui/guide.js): yellow, with
 * an arrow at what it is about, which gets a pulsing ring. Its styles are global (optimus.scss),
 * since the ring goes on elements of other pages.
 */
@Component({
  selector: 'app-tour',
  standalone: true,
  imports: [NgIf],
  template: `
    <div #bubble class="guide-bubble" role="dialog" aria-live="polite" aria-label="Optimus tour" *ngIf="view">
      <p>{{ view.text }}</p>
      <div class="guide-foot">
        <span class="guide-where">{{ view.where }}</span>
        <a href="#" class="guide-skip" (click)="skip($event)">Skip tour</a>
        <button type="button" class="guide-btn ghost" *ngIf="view.back" (click)="tour.back()">Back</button>
        <button type="button" class="guide-btn primary" *ngIf="view.next" (click)="tour.next()">{{ view.next }}</button>
      </div>
    </div>`,
})
export class TourComponent implements OnInit, OnDestroy {
  @ViewChild('bubble') private bubble?: ElementRef<HTMLElement>;
  view: View | null = null;
  private key = '';
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly onResize = () => this.tick();

  constructor(public tour: TourService, private zone: NgZone, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Outside Angular: a quarter-second check of the page would otherwise re-render all of it.
    this.zone.runOutsideAngular(() => {
      this.timer = setInterval(() => this.tick(), 250);
      addEventListener('resize', this.onResize);
    });
  }

  ngOnDestroy(): void {
    if (this.timer) { clearInterval(this.timer); }
    removeEventListener('resize', this.onResize);
  }

  skip(e: Event) {
    e.preventDefault();
    this.tour.end(false);
  }

  /** Moves on once the page shows the step is done, and keeps the note on what it is about. */
  private tick() {
    const steps = this.tour.steps();
    const at = this.tour.at();
    const step = steps[at];
    if (step && !this.tour.entering() && step.done?.(this.tour)) {
      this.zone.run(() => this.tour.next());
      return;
    }
    const target = step && !this.tour.entering() ? this.find(step) : null;
    const old = document.querySelector('.guide-target');
    if (old && old !== target) { old.classList.remove('guide-target'); }
    if (!step || !target || !(target as HTMLElement).offsetParent) { this.render(null); return; }
    target.classList.add('guide-target');

    const text = step.stuck?.(this.tour) || (typeof step.text === 'function' ? step.text(this.tour) : step.text);
    const consoleSteps = steps.filter((s) => s.part === 'console').length;
    const where = step.part === 'console' ? `The console · ${at + 1} of ${consoleSteps}`
      : `Your first test · ${at - consoleSteps + 1} of ${steps.length - consoleSteps}`;
    const lastOne = at === steps.length - 1;
    this.render({ text, where, back: step.part === 'console' && at > 0,
      next: step.part === 'console' ? (lastOne ? 'Finish' : 'Next') : step.next || null });
    this.place(target);
  }

  private find(step: TourStep): Element | null {
    return typeof step.target === 'function' ? step.target(this.tour) : document.querySelector(`[data-tour="${step.target}"]`);
  }

  private render(view: View | null) {
    const key = view ? `${this.tour.at()}|${view.text}|${view.where}|${view.back}|${view.next}` : '';
    if (key === this.key) { return; }
    this.key = key;
    this.view = view;
    this.cdr.detectChanges();
  }

  /** Below what it points at when there is room, above it otherwise; inside the foot of something
   *  tall (the chat). The arrow points at the middle of it. */
  private place(target: Element) {
    const el = this.bubble?.nativeElement;
    if (!el) { return; }
    const r = target.getBoundingClientRect();
    const b = el.getBoundingClientRect();
    const inside = r.height > innerHeight * 0.4;
    const below = !inside && r.bottom + 12 + b.height <= innerHeight - 8;
    const top = inside ? Math.min(r.bottom, innerHeight) - b.height - 14 : below ? r.bottom + 12 : Math.max(8, r.top - b.height - 12);
    const left = Math.max(8, Math.min(r.left + r.width / 2 - b.width / 2, innerWidth - b.width - 8));
    el.style.top = `${top}px`;
    el.style.left = `${left}px`;
    el.dataset['side'] = inside ? 'inside' : below ? 'below' : 'above';
    el.style.setProperty('--arrow-x', `${Math.max(18, Math.min(r.left + r.width / 2 - left, b.width - 18))}px`);
  }
}
